import {
  BackIcon,
  InputIcon,
  NumberIcon,
  DateTimeIcon,
  DropdownIcon,
  ExpressionIcon,
} from "@/components/icons";
import { builderStoreAtom, selectedGridHeaderAtom } from "@/store";
import { cn } from "@/utils/cn";
import Accordion from "@ui/accordion";
import { Checkbox } from "@ui/checkbox";
import { Input } from "@ui/input";
import { Label } from "@ui/label";
import { formatError, ValidationError } from "@/components/ui/validation-error";
import { useAtom } from "jotai";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ZodError } from "zod";
import {
  normalizeStaticDatasource,
  type GridHeaderItem,
  type GridHeaderItemPatch,
  type GridHeadersValue,
} from "../../attributes/grid-header/component/types";
import { InputHeaderFields } from "../../attributes/grid-header/component/input";
import { NumberHeaderFields } from "../../attributes/grid-header/component/number";
import { DateHeaderFields } from "../../attributes/grid-header/component/date";
import { DropdownHeaderFields } from "../../attributes/grid-header/component/dropdown";
import { RowExpressionHeaderFields } from "../../attributes/grid-header/component/row-expression";

export default function HeaderItemAttribute({
  entityId,
  headerKey,
}: {
  entityId: string;
  headerKey: string;
}) {
  const [builderStore] = useAtom(builderStoreAtom);
  const [activeHeader, setActiveHeader] = useAtom(selectedGridHeaderAtom);
  const [storeVersion, setStoreVersion] = useState(0);

  useEffect(() => {
    if (!builderStore) return;
    return builderStore.subscribe(() => {
      setStoreVersion((prev) => prev + 1);
    });
  }, [builderStore]);

  const selectedHeaderKey = activeHeader?.headerKey ?? headerKey;

  const headers = useMemo(() => {
    if (!builderStore) return [];
    const schema = builderStore.getData().schema;
    const entity = schema.entities[entityId] as
      | { attributes?: Record<string, unknown> }
      | undefined;
    const rawHeaders =
      (entity?.attributes?.gridHeaders as GridHeadersValue) ?? [];
    return Array.isArray(rawHeaders) ? rawHeaders : [];
  }, [builderStore, entityId, storeVersion]);
  const header = useMemo(
    () => headers.find((h) => h.key === selectedHeaderKey),
    [headers, selectedHeaderKey],
  );
  const headerIndex = useMemo(
    () => headers.findIndex((h) => h.key === selectedHeaderKey),
    [headers, selectedHeaderKey],
  );

  const updateHeader = async (patch: GridHeaderItemPatch) => {
    if (!builderStore || !header) return;

    const nextHeaders = headers.map((item) =>
      item.key === header.key ? { ...item, ...patch } : item,
    );

    console.debug({ nextHeaders })
    builderStore.setEntityAttribute(
      entityId,
      "gridHeaders",
      nextHeaders as any,
    );
    const result = await builderStore.validateEntityAttribute(entityId, "gridHeaders");
    console.debug({ result })

    if (patch.key && activeHeader) {
      setActiveHeader({
        ...activeHeader,
        headerKey: patch.key,
      });
    }
  };

  const replaceHeader = (nextHeader: GridHeaderItem) => {
    if (!builderStore || !header) return;

    const nextHeaders = headers.map((item) =>
      item.key === header.key ? nextHeader : item,
    );
    builderStore.setEntityAttribute(
      entityId,
      "gridHeaders",
      nextHeaders as any,
    );
    void builderStore.validateEntityAttribute(entityId, "gridHeaders");
  };

  if (!header) {
    return null;
  }

  const formattedHeaderErrors = formatError(
    headers,
    activeHeader?.header.attribute.error,
  );
  const headerError =
    formattedHeaderErrors?.[`${headerIndex}`]?.keyValue?._errors?.[0] ??
    formattedHeaderErrors?.[`${headerIndex}`]?._errors?.[0] ??
    (activeHeader?.header.attribute.error instanceof ZodError
      ? activeHeader.header.attribute.error.issues.find(
        (issue) =>
          issue.path[0] === headerIndex && issue.path[1] === "keyValue",
      )?.message
      : activeHeader?.header.attribute.error instanceof Error
        ? activeHeader.header.attribute.error.message
        : undefined);

  const typeOptions: Array<{
    value: NonNullable<GridHeaderItem["type"]>;
    label: string;
    icon: ReactNode;
  }> = [
      {
        value: "input",
        label: "Input",
        icon: <InputIcon className="text-primary-text" />,
      },
      {
        value: "number",
        label: "Number",
        icon: <NumberIcon className="text-primary-text" />,
      },
      {
        value: "date",
        label: "Date",
        icon: <DateTimeIcon className="text-primary-text" />,
      },
      {
        value: "dropdown",
        label: "Dropdown",
        icon: <DropdownIcon className="text-primary-text" />,
      },
      {
        value: "row-expression",
        label: "Row Expression",
        icon: <ExpressionIcon className="text-primary-text" />,
      },
    ];

  const flowOptions: Array<{
    value: "split" | "recursive" | "others";
    label: string;
  }> = [
      { value: "split", label: "Split" },
      { value: "recursive", label: "Recursive" },
      { value: "others", label: "Others" },
    ];

  const currentType = header.type ?? "input";

  const renderTypeFields = () => {
    switch (header.type) {
      case "input":
        return (
          <InputHeaderFields
            header={header}
            updateHeader={updateHeader}
            replaceHeader={replaceHeader}
            headerError={headerError}
          />
        );
      case "number":
        return (
          <NumberHeaderFields
            header={header}
            updateHeader={updateHeader}
            replaceHeader={replaceHeader}
            headerError={headerError}
          />
        );
      case "date":
        return (
          <DateHeaderFields
            header={header}
            updateHeader={updateHeader}
            replaceHeader={replaceHeader}
            headerError={headerError}
          />
        );
      case "dropdown":
        return (
          <DropdownHeaderFields
            header={header}
            updateHeader={updateHeader}
            replaceHeader={replaceHeader}
            headerError={headerError}
          />
        );
      case "row-expression":
        return (
          <RowExpressionHeaderFields
            header={header}
            updateHeader={updateHeader}
            replaceHeader={replaceHeader}
            headerError={headerError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="border-b border-b-stroke text-dark py-[13px] px-5 flex flex-row gap-[14px] text-base font-semibold items-center sticky top-0 bg-white z-10">
        <button
          type="button"
          onClick={() => setActiveHeader(null)}
          className="p-[6px] rounded-sm border border-stroke bg-white hover:bg-gray-2"
        >
          <BackIcon className="text-secondary-text" />
        </button>
        Edit Header Items
      </div>

      <Accordion
        items={[
          {
            key: "general",
            name: "General",
            content: (
              <div className="flex flex-col gap-4">
                <div>
                  <Label>Label</Label>
                  <Input
                    value={header.label}
                    placeholder="Label"
                    onChange={(e) => updateHeader({ label: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Key</Label>
                  <Input
                    value={header.keyValue}
                    placeholder="Key"
                    onChange={(e) =>
                      updateHeader({ keyValue: e.target.value })
                    }
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {typeOptions.map((typeItem) => {
                    const active = (header.type ?? "input") === typeItem.value;

                    return (
                      <button
                        key={typeItem.value}
                        type="button"
                        onClick={() => {
                          const nextType = typeItem.value;
                          const nextHeader = {
                            label: header.label,
                            key: header.key,
                            keyValue: header.keyValue,
                            type: nextType,
                            ...(nextType === "date" ? { subtype: "date" } : {}),
                            ...(nextType === "dropdown"
                              ? {
                                datasource: normalizeStaticDatasource(
                                  (
                                    header as GridHeaderItem & {
                                      datasource?: unknown;
                                    }
                                  ).datasource,
                                  header.keyValue,
                                ),
                              }
                              : {}),
                          } as GridHeaderItem;

                          replaceHeader(nextHeader);
                        }}
                        className={cn(
                          "w-full py-3 rounded-md border inline-flex flex-col items-center gap-1 bg-white",
                          active
                            ? "border-2 border-giant-blue bg-giant-blue/10"
                            : "border-stroke",
                        )}
                      >
                        {typeItem.icon}
                        <span className="text-xs font-medium text-dark">
                          {typeItem.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {renderTypeFields()}
              </div>
            ),
          },
          {
            key: "flow-related",
            name: "Flow Related",
            content: (
              <div className="flex flex-col gap-4">
                {flowOptions.map((item) => {
                  const currentFlowType = header.flowType ?? ["split"];
                  const checked = currentFlowType.includes(item.value);
                  return (
                    <div
                      className="flex flex-row gap-2 items-center"
                      key={item.value}
                    >
                      <Checkbox
                        id={`grid-header-flow-${item.value}`}
                        checked={checked}
                        onCheckedChange={(nextChecked) => {
                          const normalized = new Set(currentFlowType);
                          if (nextChecked) {
                            normalized.add(item.value);
                          } else {
                            normalized.delete(item.value);
                          }
                          updateHeader({
                            flowType: Array.from(normalized),
                          });
                        }}
                      />
                      <label
                        htmlFor={`grid-header-flow-${item.value}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {item.label}
                      </label>
                    </div>
                  );
                })}
              </div>
            ),
          },
        ]}
      />
    </>
  );
}
